/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useCallback } from 'preact/hooks';
import { CellPadding, TableHeaderRendererContext, SortCriterionDetail } from '.';
import { FormContext } from '../hooks/UNSAFE_useFormContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { justifyContentStyles } from '#PRIVATE_TableCommon/themes/Common.css';
import {
  tableHeaderCellTextStyles,
  tableHeaderSortContainerStyles
} from '../PRIVATE_Table/themes/Table.css';
import { tableHeaderCellStyles } from '../PRIVATE_Table/themes/TableHeaderCellStyles.css';
import {
  getFormTextAlign,
  getHeaderFooterCellClassArray
} from '../PRIVATE_Table/utils/TableThemeUtils';
import { SortControl } from '../UNSAFE_SortControl';
import { SortableContext } from '#hooks/UNSAFE_useSortableContext';

/**
 * Props for the TableDisplayHeaderCell Component
 */
export type TableDisplayHeaderCellProps<C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSticky: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  isRtl: boolean;
  isAccColumnHeader: boolean;
  isHighContrast: boolean;
  headerText?: string;
  stickyTopOffset?: number;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  renderer?: (context: TableHeaderRendererContext<C>) => ComponentChildren;
  sortable?: 'enabled' | 'disabled';
  sortDirection?: 'ascending' | 'descending' | 'unsorted';
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  padding?: CellPadding;
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

/**
 * The internal component used to render a single cell in a TableDisplayHeaderRow.
 */
export function TableDisplayHeaderCell<C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  isSticky,
  headerText,
  stickyTopOffset = 0,
  renderer,
  sortable = 'disabled',
  sortDirection = 'unsorted',
  onSortCriterionChange,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  isAccColumnHeader,
  isHighContrast,
  padding,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableDisplayHeaderCellProps<C>) {
  const cellClasses = classNames(
    getHeaderFooterCellClassArray({
      isHeader: true,
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      hasVerticalGridlines,
      isSticky,
      isStickyStartEdge,
      isStickyEndEdge,
      hasStartEdge,
      hasEndEdge,
      isRtl,
      horizontalAlignment,
      verticalAlignment,
      isHighContrast,
      padding
    })
  );

  const handleSortGesture = useCallback(() => {
    if (onSortCriterionChange != null) {
      const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
      onSortCriterionChange([{ key: columnKey, sortDirection: newDirection }]);
    }
  }, [columnKey, sortDirection, onSortCriterionChange]);

  const headerCellRendererProps = {
    key: columnKey,
    headerText: headerText,
    sortable: sortable,
    sortDirection: sortDirection
  };

  const getHeaderCellRenderer = useCallback(() => {
    if (renderer != null) {
      return renderer;
    }
    return ({ headerText, sortable, sortDirection }: TableHeaderRendererContext<C>) => {
      const text =
        headerText != null ? (
          <div class={tableHeaderCellTextStyles.base}>
            <span class={tableHeaderCellStyles.defaultRendererSpan}>{headerText}</span>
          </div>
        ) : undefined;
      if (sortable === 'enabled' || sortDirection !== 'unsorted') {
        const isIconFirst =
          horizontalAlignment === 'end' ||
          (!isRtl && horizontalAlignment === 'right') ||
          (isRtl && horizontalAlignment === 'left');

        const containerClasses = classNames([
          tableHeaderSortContainerStyles.base,
          justifyContentStyles[horizontalAlignment]
        ]);
        return (
          <div class={containerClasses}>
            {isIconFirst && <SortControl />}
            {text}
            {!isIconFirst && <SortControl />}
          </div>
        );
      }
      return text;
    };
  }, [renderer, horizontalAlignment, isRtl]);

  const getAriaSort = () => {
    if (sortDirection === 'ascending' || sortDirection === 'descending') {
      return { 'aria-sort': sortDirection };
    }
    return undefined;
  };

  return (
    <SortableContext.Provider
      value={{
        isRendered: sortable === 'enabled',
        sortable,
        sortDirection,
        onSort: handleSortGesture,
        isTabbable: true
      }}>
      <th
        aria-colindex={columnIndex + 1}
        {...getAriaSort()}
        class={cellClasses}
        style={`top:${stickyTopOffset}px;`}
        role={isAccColumnHeader ? 'columnheader' : 'cell'}
        data-oj-cell-type={'header'}
        data-oj-column-key={columnKey}
        data-oj-table-focusable={tableId}
        data-oj-table-header={tableId}>
        <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
          {getHeaderCellRenderer()(headerCellRendererProps)}
        </FormContext.Provider>
      </th>
    </SortableContext.Provider>
  );
}
